import { AbstractComponent } from "appworks/components/abstract-component";
import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { SpineContainer } from "appworks/graphics/pixi/spine-container";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { shuffle } from "appworks/utils/collection-utils";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { RandomRangeInt } from "appworks/utils/math/random";
import { Timer } from "appworks/utils/timer";
import { Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";

export class FowlFlingBonusTransitionComponent extends AbstractComponent {
    protected layer = gameLayers.FlingBonusTransition;
    protected footstepInterval: number;

    public playFlingIntro(): Contract {
        const sound = Services.get(SoundService);

        let foxIntro: SpineContainer;
        let chickenMarch: SpineContainer;
        let coop: SpineContainer;
        let grabMask: Sprite;

        return new Sequence([
            () => this.layer.setScene("fling_in"),
            () =>
                Contract.wrap(() => {
                    coop = gameLayers.FlingBonus.getSpine("coop");
                    chickenMarch = gameLayers.FlingBonus.getSpine("faf_chicken_march");

                    grabMask = this.layer.getSprite("grab_mask");
                    grabMask.visible = false;

                    foxIntro = this.layer.getSpine("fox_intro");
                    foxIntro.play("03_sneak_egg");

                    const startPos = this.layer.getPosition("fox_intro_start");
                    foxIntro.setDualTransform(startPos);

                    this.startFootstepSounds();
                }),
            () => {
                const targetPos = this.layer.getPosition("fox_intro_grab");
                return new Parallel([
                    () => TweenContract.wrapTween(new Tween(foxIntro.landscape).to({ x: targetPos.landscape.x, y: targetPos.landscape.y }, 3333)),
                    () => TweenContract.wrapTween(new Tween(foxIntro.portrait).to({ x: targetPos.portrait.x, y: targetPos.portrait.y }, 3333)),
                ]);
            },
            () =>
                Contract.wrap(() => {
                    grabMask.visible = true;
                    foxIntro.mask = grabMask;
                    this.stopFootstepSounds();
                }),
            () =>
                Contract.wrap(() => {
                    sound.customEvent("faf_intro_grab");
                    Timer.setTimeout(() => sound.customEvent("faf_intro_pull"), 1500);
                }),
            () => foxIntro.playOnce("04_caught"),
            () => Contract.wrap(() => sound.customEvent("faf_coop_shake")),
            () => coop.playOnce("coop_shaking"),
            () => Contract.wrap(() => chickenMarch.play("walk_cycle")),
            () => {
                const targetPos = gameLayers.FlingBonus.getPosition("chicken_march_end");
                return new Parallel([
                    () => TweenContract.wrapTween(new Tween(chickenMarch.landscape).to({ x: targetPos.landscape.x, y: targetPos.landscape.y }, 1866)),
                    () => TweenContract.wrapTween(new Tween(chickenMarch.portrait).to({ x: targetPos.portrait.x, y: targetPos.portrait.y }, 1866)),
                ]);
            },
            () =>
                Contract.wrap(() => {
                    chickenMarch.play("wait_for_kick");
                    Timer.setTimeout(() => sound.customEvent("faf_gloveshove"), 700);
                }),
            () => coop.playOnce("chinken_push"),
            () => Contract.wrap(() => coop.play("glove_idle")),
            () => this.layer.defaultScene(),
        ]);
    }

    public playPickOutro(): Contract {
        const sound = Services.get(SoundService);

        let foxIntro: SpineContainer;
        let egg: SpineContainer = gameLayers.EggPickBonus.container.getChildByName("fox_egg") as SpineContainer;

        let startPos: DualPosition;
        let endPos: DualPosition;

        return new Sequence([
            () => this.layer.setScene("pick_out"),
            () =>
                Contract.wrap(() => {
                    foxIntro = this.layer.getSpine("fox_intro");
                    foxIntro.play("01_sneak_noegg");

                    startPos = this.layer.getPosition("fox_intro_start");
                    endPos = this.layer.getPosition("fox_intro_end");

                    foxIntro.setDualTransform(startPos);

                    this.startFootstepSounds();
                }),
            () =>
                new Parallel([
                    () => TweenContract.wrapTween(new Tween(foxIntro.landscape).to({ x: egg.landscape.x + startPos.landscape.width / 2 }, 2000)),
                    () => TweenContract.wrapTween(new Tween(foxIntro.portrait).to({ x: egg.portrait.x + startPos.landscape.width / 2 }, 2000)),
                ]),
            () =>
                new Parallel([
                    () => foxIntro.playOnce("02_stop_twist"),
                    () => Contract.wrap(() => Timer.setTimeout(() => sound.customEvent("faf_intro_spin"), (1000 / 30) * 25)),
                    () => Contract.wrap(() => Timer.setTimeout(() => sound.customEvent("faf_egg_twinkle"), (1000 / 30) * 46)),
                    () => Contract.wrap(() => Timer.setTimeout(() => sound.customEvent("faf_fox_hehe"), (1000 / 30) * 83)),
                    () => Contract.wrap(() => Timer.setTimeout(() => (egg.visible = false), 1000)),
                    () => Contract.wrap(() => this.stopFootstepSounds()),
                ]),
            () => {
                this.startFootstepSounds();
                foxIntro.play("03_sneak_egg");
                return new Parallel([
                    () => TweenContract.wrapTween(new Tween(foxIntro.landscape).to({ x: endPos.landscape.x }, 2000)),
                    () => TweenContract.wrapTween(new Tween(foxIntro.portrait).to({ x: endPos.portrait.x }, 2000)),
                ]);
            },
            () => Contract.wrap(() => this.stopFootstepSounds()),
            () => this.layer.defaultScene(),
        ]);
    }

    protected startFootstepSounds() {
        const sound = Services.get(SoundService);
        let footstepSoundQueue: number[] = [];
        this.footstepInterval = Timer.setInterval(() => {
            if (footstepSoundQueue.length == 0) {
                footstepSoundQueue = [4, 3, 2, 1];
            }
            sound.customEvent("faf_intro_footstep_" + footstepSoundQueue.pop());
        }, 166);
    }

    protected stopFootstepSounds() {
        Timer.clearInterval(this.footstepInterval);
    }
}
